import { useState } from "react"
import { ChevronDown, Store } from "lucide-react"

import { cn } from "@/lib/utils"

type WarehouseAddress = {
  street?: string | null
  externalNumber?: string | null
  internalNumber?: string | null
  neighborhood?: string | null
  city?: string | null
  state?: string | null
  zipCode?: string | null
}

type WarehouseSummary = {
  name?: string | null
  address?: WarehouseAddress | null
}

interface WarehouseDisplayProps {
  warehouse?: WarehouseSummary | null
  fallbackName?: string
  isExpanded?: boolean
  onToggle?: () => void
  defaultExpanded?: boolean
  className?: string
  triggerClassName?: string
  detailsClassName?: string
}

export function WarehouseDisplay({
  warehouse,
  fallbackName = "Almacén",
  isExpanded,
  onToggle,
  defaultExpanded = false,
  className,
  triggerClassName,
  detailsClassName,
}: WarehouseDisplayProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
  const address = warehouse?.address
  const warehouseName = warehouse?.name || fallbackName
  const location = [address?.city, address?.state].filter(Boolean).join(", ")
  const hasDetails = Boolean(address)
  const isControlled = isExpanded !== undefined
  const expanded = isControlled ? isExpanded : internalExpanded
  const canToggle = hasDetails && (!isControlled || Boolean(onToggle))

  const handleToggle = () => {
    if (!canToggle) return
    onToggle?.()
    if (!isControlled) {
      setInternalExpanded((current) => !current)
    }
  }

  const streetLine = [address?.street, address?.externalNumber].filter(Boolean).join(" ")
  const fullStreetLine = address?.internalNumber
    ? `${streetLine}, Int. ${address.internalNumber}`
    : streetLine
  const cityLine = [address?.city, address?.state].filter(Boolean).join(", ")
  const cityZipLine = [cityLine, address?.zipCode].filter(Boolean).join(" ")

  return (
    <div className={cn("min-w-0", className)}>
      <button
        type="button"
        onClick={canToggle ? handleToggle : undefined}
        disabled={!canToggle}
        aria-expanded={canToggle ? expanded : undefined}
        className={cn(
          "flex min-w-0 items-center gap-1.5 text-left text-xs font-bold uppercase tracking-widest text-cyan-600 transition-colors hover:text-cyan-500 disabled:cursor-default disabled:hover:text-cyan-600",
          triggerClassName
        )}
      >
        <Store className="h-3 w-3 shrink-0" />
        <span className="min-w-0 truncate">
          {warehouseName}
          {location && <span className="text-cyan-700/80"> - {location}</span>}
        </span>
        {hasDetails && (
          <ChevronDown
            className={cn("h-3 w-3 shrink-0 transition-transform duration-200", expanded && "rotate-180")}
          />
        )}
      </button>

      {hasDetails && expanded && (
        <div
          className={cn(
            "mt-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5 px-4 py-3 text-xs leading-relaxed text-muted-foreground",
            detailsClassName
          )}
        >
          <p className="font-bold uppercase tracking-widest text-foreground">{warehouseName}</p>
          {fullStreetLine && <p>{fullStreetLine}</p>}
          {address?.neighborhood && <p>{address.neighborhood}</p>}
          {cityZipLine && <p>{cityZipLine}</p>}
        </div>
      )}
    </div>
  )
}
